import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useGuardianTransactionStatus } from '@/hooks/use-guardian-transaction-status';
import { cn } from '@/lib/utils';
import { type GuardianTransactionStatus } from '@mls-wallet/shared-types';
import {
  getExecutionAutoDismissDelay,
  getExecutionStatusMessage,
  getExecutionSubject,
  isExecutionDismissible,
  PARTIAL_EXECUTION_USER_MESSAGE,
} from './execution-status-display';
import type { ExecutionEntry } from './execution-state';

const POLLING_ERROR_MESSAGE = 'Failed to fetch execution status';
const MAX_CONSECUTIVE_POLLING_FAILURES = 3;

interface ExecutionTrayProps {
  executions: ExecutionEntry[];
  markExecutionCompleted: (id: string) => void;
  removeExecution: (id: string) => void;
}

interface ExecutionTrayItemProps {
  execution: ExecutionEntry;
  markExecutionCompleted: (id: string) => void;
  removeExecution: (id: string) => void;
}

interface ExecutionTrayCardProps {
  status: GuardianTransactionStatus | undefined;
  hasError: boolean;
  hasFailed: boolean;
  isDismissible: boolean;
  message: string | null;
  onDismiss?: () => void;
  title: string;
}

interface ResolveErrorMessageOptions {
  guardianErrorMessage: string | null;
  hasPollingError: boolean;
  immediateError: string | null;
  requiresSupport: boolean;
}

/**
 * Resolves competing error sources in customer-impact order.
 *
 * A request that never started wins over polling failures, partial execution
 * guidance, and finally the GuardianTransaction-specific message.
 */
function resolveErrorMessage({
  guardianErrorMessage,
  hasPollingError,
  immediateError,
  requiresSupport,
}: ResolveErrorMessageOptions): string | null {
  // The execute request itself failed before polling could begin.
  if (immediateError) return immediateError;
  // Repeated status requests failed after the execution was accepted.
  if (hasPollingError) return POLLING_ERROR_MESSAGE;
  // Some networks succeeded, so generic guardian errors must not hide support guidance.
  if (requiresSupport) return PARTIAL_EXECUTION_USER_MESSAGE;
  // Otherwise the GuardianTransaction status owns the displayed explanation.
  return guardianErrorMessage;
}

/**
 * Selects the visual status cue for an execution without deciding its copy or
 * dismissal behavior.
 */
function ExecutionIcon({
  status,
  hasError,
}: {
  status: GuardianTransactionStatus | undefined;
  hasError: boolean;
}) {
  if (hasError) {
    return <XCircle className="h-5 w-5 shrink-0 text-red-500" />;
  }

  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="h-5 w-5 shrink-0 text-green-500" />;
    case 'PARTIALLY_EXECUTED':
    case 'REVERTED':
      return <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />;
    case 'FAILED':
      return <XCircle className="h-5 w-5 shrink-0 text-red-500" />;
    case 'BLOCKED':
      return <XCircle className="h-5 w-5 shrink-0 text-gray-500" />;
    default:
      return <Loader2 className="h-5 w-5 shrink-0 animate-spin text-primary" />;
  }
}

/**
 * Renders the fixed presentation shared by every execution state.
 *
 * Behavior stays in ExecutionTrayItem so this component remains a small,
 * deterministic view of already-derived state.
 */
function ExecutionTrayCard({
  status,
  hasError,
  hasFailed,
  isDismissible,
  message,
  onDismiss,
  title,
}: ExecutionTrayCardProps) {
  return (
    <Card
      className={cn(
        'relative w-[340px] gap-0 py-0 shadow-lg',
        hasFailed && 'bg-red-50 dark:bg-red-950/20'
      )}
    >
      <CardContent className="flex gap-3 py-3 pl-4 pr-10">
        <ExecutionIcon status={status} hasError={hasError} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
            {message}
          </p>
        </div>
        {isDismissible && onDismiss && (
          <Button
            aria-label="Dismiss execution"
            className="absolute right-2 top-2 h-7 w-7"
            onClick={onDismiss}
            size="icon"
            variant="ghost"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Polls one execution to a terminal state, runs terminal callbacks exactly
 * once, and marks completion only after callbacks settle before success-only
 * auto-dismissal.
 */
function ExecutionTrayItem({
  execution,
  markExecutionCompleted,
  removeExecution,
}: ExecutionTrayItemProps) {
  const callbackInvokedRef = useRef(false);
  const consecutivePollingFailuresRef = useRef(0);
  const [hasPollingError, setHasPollingError] = useState(false);

  // Poll until terminal, derive the visible phase, invoke callbacks once, then
  // retain failures or auto-dismiss a successful execution.
  const { data, error } = useGuardianTransactionStatus(
    execution.immediateError || hasPollingError
      ? null
      : execution.guardianTransactionId
  );

  const status = data?.status;
  const hasImmediateError = !!execution.immediateError;
  const isEffectivelyFinished = isExecutionDismissible(
    status,
    hasImmediateError,
    hasPollingError
  );
  const isSuccess =
    !hasImmediateError && !hasPollingError && status === 'COMPLETED';
  const requiresSupport =
    !hasImmediateError && !hasPollingError && status === 'PARTIALLY_EXECUTED';
  const hasFailed = isEffectivelyFinished && !isSuccess && !requiresSupport;
  const autoDismissDelay = getExecutionAutoDismissDelay(
    status,
    hasImmediateError,
    hasPollingError
  );

  // Treat isolated network failures as transient. SWR retains the polling key
  // and performs its normal retries until the bounded failure threshold.
  useEffect(() => {
    if (data && !error) {
      consecutivePollingFailuresRef.current = 0;
      return;
    }

    if (!error || hasPollingError) return;

    consecutivePollingFailuresRef.current += 1;
    if (
      consecutivePollingFailuresRef.current >= MAX_CONSECUTIVE_POLLING_FAILURES
    ) {
      setHasPollingError(true);
    }
  }, [data, error, hasPollingError]);

  // Invoke consumer callbacks exactly once after any terminal outcome. Keep
  // the entry active until those callbacks settle so originating controls do
  // not unblock before their refreshed server state has arrived.
  useEffect(() => {
    // Case: only terminal outcomes whose callbacks have not run continue.
    if (!isEffectivelyFinished || callbackInvokedRef.current) return;
    callbackInvokedRef.current = true;

    const runCallbacks = async () => {
      try {
        // Case: a successful terminal execution runs the success callback.
        if (isSuccess) {
          await execution.callbacks.onSuccess?.(data?.createdRecordId ?? null);
        } else {
          const errorMessage = resolveErrorMessage({
            guardianErrorMessage: data?.errorMessage ?? null,
            hasPollingError,
            immediateError: execution.immediateError,
            requiresSupport,
          });
          await execution.callbacks.onError?.(errorMessage);
        }
        await execution.callbacks.onComplete?.();
      } finally {
        // Refresh callbacks own the handoff from local execution state to the
        // server-polled terminal state, even when a callback itself fails.
        markExecutionCompleted(execution.id);
      }
    };

    void runCallbacks().catch((callbackError: unknown) => {
      console.error('Execution completion callback failed', callbackError);
    });
  }, [
    data,
    execution,
    hasImmediateError,
    hasPollingError,
    isEffectivelyFinished,
    isSuccess,
    markExecutionCompleted,
    requiresSupport,
  ]);

  // Successful entries fade automatically; failures remain available for the
  // user to read and dismiss manually. Wait for terminal callbacks so removal
  // cannot release the local execution lock before refreshed state arrives.
  useEffect(() => {
    if (autoDismissDelay === null || execution.completedAt === null) return;

    const timeoutId = window.setTimeout(
      () => removeExecution(execution.id),
      autoDismissDelay
    );
    return () => window.clearTimeout(timeoutId);
  }, [autoDismissDelay, execution.completedAt, execution.id, removeExecution]);

  const executionNoun = getExecutionSubject(
    execution.executionTarget,
    execution.proposalType
  );
  const capitalizedExecutionNoun =
    executionNoun.charAt(0).toUpperCase() + executionNoun.slice(1);

  // Derive title and body independently so specialized support guidance can
  // coexist with the correct proposal or transaction noun.
  const title = isSuccess
    ? `${capitalizedExecutionNoun} complete`
    : requiresSupport
      ? `Something went wrong with this ${executionNoun}`
      : isEffectivelyFinished
        ? `${capitalizedExecutionNoun} failed`
        : `Executing ${executionNoun}`;
  const message = resolveErrorMessage({
    guardianErrorMessage: getExecutionStatusMessage(
      status,
      data?.errorMessage,
      execution.executionTarget
    ),
    hasPollingError,
    immediateError: execution.immediateError,
    requiresSupport,
  });

  return (
    <ExecutionTrayCard
      status={status}
      hasError={hasImmediateError || hasPollingError}
      hasFailed={hasFailed}
      isDismissible={isEffectivelyFinished}
      message={message}
      onDismiss={() => removeExecution(execution.id)}
      title={title}
    />
  );
}

/**
 * Renders the global, bottom-right stack of active executions.
 *
 * The container is one polite live region so additions and status changes are
 * announced without creating nested, duplicate announcements per card.
 */
export function ExecutionTray({
  executions,
  markExecutionCompleted,
  removeExecution,
}: ExecutionTrayProps) {
  return (
    <div
      aria-label="Active executions"
      className="fixed bottom-4 right-4 z-[100] flex max-h-[calc(100vh-2rem)] flex-col gap-3 overflow-y-auto"
      role="status"
    >
      <AnimatePresence initial={false}>
        {executions.map((execution) => (
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{
              opacity: 0,
              transition: { duration: 0.4, ease: 'easeInOut' },
            }}
            initial={{ opacity: 0, scale: 0.98, y: 8 }}
            key={execution.id}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <ExecutionTrayItem
              execution={execution}
              markExecutionCompleted={markExecutionCompleted}
              removeExecution={removeExecution}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
